// Shared task/priority/status iconography.
//
// Replaces the duplicated emoji maps (🔴🟠🟡🟢⚪ priority, ○◑◕⊘● status,
// 🔵🟡✅🔴 milestone) that were copy-pasted across TaskDetailPage,
// KanbanPage, ProjectDetailPage and EntityDetailPage. Lucide SVGs render
// consistently, scale via `size`, and theme via `color`.
//
// Note: these render as SVG elements, so they can be used anywhere a node is
// allowed — but NOT inside a native <option>, which only renders text. For
// <select> options, use a plain text label (no icon).
import type { LucideIcon } from 'lucide-react';
import {
    Circle, CircleDot, Eye, Ban, CircleCheck, CircleAlert,
    Folder, Rocket, Lightbulb, Building2, CheckCircle2, Target,
    FlaskConical, BarChart2, Wrench, Star, FolderOpen, Smartphone, Palette,
} from 'lucide-react';
import { priorityColor, statusColor } from '../utils/statusColors';

// ─── Priority ─────────────────────────────────────────────────────────────────

// Colored dot — filled for a set priority, hollow for "none".
export function PriorityIcon({ priority, size = 12 }: { priority: string; size?: number }) {
    const color = priorityColor(priority);
    const filled = priority !== 'none';
    // Colors go through CSS `style` (not the color/fill props): Lucide props
    // become SVG presentation attributes, where Firefox never resolves var().
    return (
        <Circle
            size={size}
            strokeWidth={filled ? 0 : 2}
            aria-hidden="true"
            style={{ flexShrink: 0, color, fill: filled ? color : 'transparent' }}
        />
    );
}

// ─── Task status ──────────────────────────────────────────────────────────────

const STATUS_ICONS: Record<string, LucideIcon> = {
    todo: Circle,
    in_progress: CircleDot,
    review: Eye,
    blocked: Ban,
    done: CircleCheck,
};

export function StatusIcon({ status, size = 14 }: { status: string; size?: number }) {
    const Icon = STATUS_ICONS[status] ?? Circle;
    return <Icon size={size} aria-hidden="true" style={{ flexShrink: 0, color: statusColor(status) }} />;
}

// ─── Milestone status ─────────────────────────────────────────────────────────

const MS_STATUS_ICONS: Record<string, LucideIcon> = {
    upcoming: Circle,
    in_progress: CircleDot,
    completed: CircleCheck,
    overdue: CircleAlert,
};

const MS_STATUS_COLORS: Record<string, string> = {
    upcoming: 'var(--accent)',
    in_progress: 'var(--warn)',
    completed: 'var(--ok)',
    overdue: 'var(--danger)',
};

export function MilestoneStatusIcon({ status, size = 14 }: { status: string; size?: number }) {
    const Icon = MS_STATUS_ICONS[status] ?? Circle;
    const color = MS_STATUS_COLORS[status] ?? 'var(--ink-muted)';
    return <Icon size={size} aria-hidden="true" style={{ flexShrink: 0, color }} />;
}

// ─── Entity / Project icon picker ─────────────────────────────────────────────

export const PICKER_ICONS: Record<string, LucideIcon> = {
    folder: Folder,
    rocket: Rocket,
    lightbulb: Lightbulb,
    building: Building2,
    check: CheckCircle2,
    target: Target,
    flask: FlaskConical,
    chart: BarChart2,
    wrench: Wrench,
    star: Star,
    smartphone: Smartphone,
    palette: Palette,
};

export function EntityIcon({ icon, size = 16 }: { icon?: string | null; size?: number }) {
    const Mapped = icon ? PICKER_ICONS[icon] : undefined;
    if (Mapped) return <Mapped size={size} aria-hidden="true" />;
    if (icon) return <span aria-hidden="true">{icon}</span>;
    return <FolderOpen size={size} aria-hidden="true" />;
}
